import { Group, Paper, Switch, Text, Tooltip } from "@mantine/core";
import clsx from "clsx";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { createGrid, gridStats } from "./grid.ts";
import { FieldDeposit, GridCell, ProspectingEvent } from "./types.ts";
import style from "./SystemGrid.module.css";

const getIcon = (uid: string) => {
  if (!uid.startsWith("16:")) {
    return "";
  }
  const id = uid.substring(3);
  return `https://img.swcombine.com//materials/${id}/deposit.gif`;
};

interface CellContentProps {
  cell: GridCell;
  onlyTraces: boolean;
  materialNames: Record<string, string>;
}

function CellContent({ cell, onlyTraces, materialNames }: CellContentProps) {
  const { asteroid, deposit, prospected, possibleEventKeys, indirectFound, indirectFromEventKeys, depositExcluded, found } = cell;

  let content: ReactNode = null;
  let classes = "";

  if (deposit && !onlyTraces) {
    const label = `${deposit.size.toLocaleString()} units of ${materialNames[deposit.type] ?? deposit.type}`;
    const iconUrl = getIcon(deposit.type);
    content = (
      <Tooltip label={label}>
        {iconUrl ? <img src={iconUrl} alt={deposit.type} /> : <span>?</span>}
      </Tooltip>
    );
    classes = style.found;
  } else if (found && !onlyTraces) {
    content = <span title="Deposit found">?</span>;
    classes = style.found;
  } else if (prospected) {
    content = <span title="Prospected">P{(cell.trace ?? -1) < 0 ? "X" : cell.trace}</span>;
    classes = style.prospected;
  } else if (depositExcluded) {
    content = <span title="Deposit excluded">X</span>;
    classes = style.excluded;
  } else if (indirectFound && !onlyTraces) {
    const evidence = indirectFromEventKeys?.length ?? 1;
    content = (
      <span title={`Indirect found (${evidence} trace constraint${evidence > 1 ? "s" : ""})`}>
        !
      </span>
    );
    classes = style.indirect;
  } else if (possibleEventKeys) {
    const evidence = possibleEventKeys.length;
    content = <span title={`Possible deposit (${evidence} unresolved trace${evidence > 1 ? "s" : ""})`}>?</span>;
    classes = style.possible;
  } else if (asteroid) {
    classes = style.asteroid;
  }

  return <td className={clsx(style.cell, classes)}>{content}</td>;
}

interface SystemGridProps {
  layout: string;
  deposits: FieldDeposit[];
  events: ProspectingEvent[];
  materialNames: Record<string, string>;
}

export default function SystemGrid({ layout, deposits, events, materialNames }: SystemGridProps) {
  const [onlyTraces, setOnlyTraces] = useState(false);

  const grid = useMemo(() => createGrid(layout, events, deposits), [layout, events, deposits]);
  const counts = useMemo(() => gridStats(grid), [grid]);

  return (
    <Paper p="sm" withBorder>
      <div className={style.tableWrapper}>
        <table className={style.systemGrid}>
          <thead>
            <tr>
              <th />
              {grid.map((_, idx) => (
                <th key={`top-${idx}`}>{idx}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {grid.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                <td>{rowIndex}</td>
                {row.map((cell, colIndex) => (
                  <CellContent
                    key={`${rowIndex}-${colIndex}`}
                    cell={cell}
                    onlyTraces={onlyTraces}
                    materialNames={materialNames}
                  />
                ))}
                <td>{rowIndex}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th />
              {grid.map((_, idx) => (
                <th key={`bottom-${idx}`}>{idx}</th>
              ))}
              <th />
            </tr>
          </tfoot>
        </table>
      </div>

      <Group mt="sm" justify="space-between" align="start">
        <div>
          <Text size="sm">Total asteroids: {counts.total}</Text>
          <Text size="sm">Prospected: {counts.prospected}</Text>
          <Text size="sm">
            Missing: {counts.empty} ({counts.total > 0 ? ((counts.empty / counts.total) * 100).toFixed(2) : "0.00"}%)
          </Text>
        </div>
        <Switch
          checked={onlyTraces}
          onChange={(e) => setOnlyTraces(e.currentTarget.checked)}
          label="Only show traces"
        />
      </Group>

      <Group className={style.legend} mt="sm">
        <div className={clsx(style.legendItem, style.asteroid)}>Unscanned asteroid</div>
        <div className={clsx(style.legendItem, style.prospected)}>Prospected, no deposit</div>
        <div className={clsx(style.legendItem, style.excluded)}>No possible deposit</div>
        <div className={clsx(style.legendItem, style.possible)}>Possible deposit</div>
        <div className={clsx(style.legendItem, style.indirect)}>Indirect found</div>
        <div className={clsx(style.legendItem, style.found)}>Deposit found</div>
      </Group>
    </Paper>
  );
}
